import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AgendaDia, AgendaDiaResumo } from '../../interfaces/agenda-dia';
import { AgendaFilaResult } from '../../interfaces/agenda-fila-result';
import { SlotDisponivelItem } from '../../interfaces/slot-disponivel';

@Injectable({
  providedIn: 'root',
})
export class AgendaFilaService {
  private apiURL = `${environment.apiUrl}/v${environment.apiVersion}/AgendaFila`;

  constructor(private httpClient: HttpClient) {}

  public async getAgendasDia(cdEstabelecimento: number, cdEspecialidade?: number, excluirOdonto = false): Promise<AgendaDia[]> {
    let url = `${this.apiURL}/agendas-dia?cdEstabelecimento=${cdEstabelecimento}`;
    if (cdEspecialidade) url += `&cdEspecialidade=${cdEspecialidade}`;
    if (excluirOdonto) url += `&excluirOdonto=true`;
    const response = await firstValueFrom(
      this.httpClient
        .get<any>(url)
        .pipe(catchError(this.handleError))
    );
    return response?.data ?? [];
  }

  /** Agendas que funcionam no dia na unidade, com vagas/agendadas/livres. data: yyyy-MM-dd. */
  public async getAgendasDiaResumo(cdEstabelecimento: number, data: string): Promise<AgendaDiaResumo[]> {
    const response = await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/agendas-dia-resumo?cdEstabelecimento=${cdEstabelecimento}&data=${data}`)
        // 404 = nenhuma agenda funciona no dia; a tela mostra vazio, não erro.
        .pipe(catchError((error: HttpErrorResponse) => error.status === 404 ? of({ data: [] }) : this.handleError(error)))
    );
    return response?.data ?? [];
  }

  public async getAgendaFila(cdEstabelecimento: number, cdAgenda: number): Promise<AgendaFilaResult> {
    const response = await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/agenda-fila?cdEstabelecimento=${cdEstabelecimento}&cdAgenda=${cdAgenda}`)
        .pipe(catchError(this.handleError))
    );
    return response?.data?.[0] ?? { fila: [], tempoMedio: null };
  }

  public async getSlotsDisponiveis(cdEstabelecimento: number, cdAgenda?: number, cdEspecialidade?: number): Promise<SlotDisponivelItem[]> {
    let url = `${this.apiURL}/slots-disponiveis?cdEstabelecimento=${cdEstabelecimento}`;
    if (cdAgenda) url += `&cdAgenda=${cdAgenda}`;
    if (cdEspecialidade) url += `&cdEspecialidade=${cdEspecialidade}`;
    const response = await firstValueFrom(
      this.httpClient.get<any>(url).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            const feedback = error.error?.feedback as string | undefined;
            return of({ data: [], feedback });
          }
          console.error('AgendaFilaService error:', error);
          return throwError(() => error.message);
        })
      )
    );
    return response?.data ?? [];
  }

  public async agendarPacienteSlot(nrSequencia: number, cdPessoaFisica: number, nmUsuario: string): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/agendar-paciente`, { nrSequencia, cdPessoaFisica, nmUsuario })
        .pipe(catchError(this.handleError))
    );
  }

  private handleError(error: HttpErrorResponse) {
    console.error('AgendaFilaService error:', error);
    return throwError(() => error.message);
  }
}
